import { html, css, LitElement } from "lit";
import { customElement } from "lit/decorators.js";
import { state } from "lit/decorators.js";
import { toSanitizedMarkdownHtml } from "../markdown.ts";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

const EXPLORE_TYPES = [
  { key: "pfm-full", label: "PFM Full", desc: "Nodes, Features & Test Cases" },
  { key: "pfm-spec", label: "PFM Spec", desc: "Nodes, Features & Functional Steps" },
  { key: "pfm-cs", label: "PFM CS", desc: "Nodes, Features & CS Functional Steps" },
  { key: "website", label: "Website", desc: "Content & Information Gathering" },
  { key: "other", label: "Other", desc: "Custom Goal" },
] as const;

/** PFM explore types: Explore prompt only (login credentials are per project template). */
const PART_LABELS_PFM = [
  {
    key: "part2",
    label: "Explore",
    title: "Explore",
    placeholder: "PFM node identification, DFS navigation, tree construction...",
  },
];

const PART_LABELS_SIMPLE = [
  { key: "part1", label: "Runtime", title: "Runtime Tasks", placeholder: "What to explore and do during the run..." },
  { key: "part2", label: "Reporting", title: "Reporting", placeholder: "What to include in the final report..." },
];

export interface ExploreTypePrompt {
  part1?: string;
  part2?: string;
  part3?: string;
  part4?: string;
}

const STORAGE_KEY = "ead-explore-type-prompts-v2";

const DEFAULT_PROMPTS: Record<string, ExploreTypePrompt> = {
  "pfm-full": {
    part2: "Study the application for its product functions—the PFM describes product functions, not sample data used to showcase the app. Sample data can look like product functionality, so explicitly tell the difference between real product functions and example/demo records.\n\nPresent the PFM as a multi-level hierarchy in mindmaps and deliverables (parents with children beneath, several depths—not one flat ring of nodes) so end users can understand features from broad areas down to specifics.\n\nPFM Node Validity Rules (all required):\nA PFM node is used to represent a component or a functional area of a product or a system.\n\nA valid PFM node can be a view or page of a software product where one or a few specific functions exist. It must satisfy all of these rules:\n- Specific Function: The node has a distinct, identifiable operation or functional purpose.\n- Quantifiable Outcome: The node produces or supports observable outcomes and can often be further represented by a few features and functions.\n- Product Function Focus: Explore product functions, not sample data. Sample data can feel like product functions; identify and explain the difference before naming a node.\n- Testable: The node can be verified by manual or automated test cases.\n\nDo not create PFM nodes for simple actions such as clicking a button, typing into a field, opening a dropdown, scrolling, or navigating. These belong inside test steps under the relevant feature or test case.\n\nPFM Node Classification: Level 1 Product/System Domain → Level 2 Component or Functional Area → Level 3 Feature or Function → Test Cases → Test Steps.\nNavigation: Top-down exploration, URL state tracking, modal handling, AJAX detection.\nCompletion: Node saturation, ≥90% leaf coverage, dead-end resolution.\n\nEvidence: Whenever a PFM node or feature is newly identified or re-confirmed, capture a screenshot, include it with your step report (e.g. report_running_step), and ensure it is recorded so the evidence stays attached to that finding.",
  },
  "pfm-spec": {
    part2: "Same as PFM Full Explore. Focus on functional specifications instead of test cases.",
  },
  "pfm-cs": {
    part2: "Same as PFM Full Explore, with CS focus.",
  },
  "website": { part1: "Explore the target website: main sections, navigation, key pages, interactive elements.", part2: "Summary, key pages, organized findings, observations." },
  "other": { part1: "Define runtime tasks for the custom goal.", part2: "Define reporting format and deliverables." },
};

function isPfmExploreType(typeKey: string): boolean {
  return !["website", "other"].includes(typeKey);
}

export function getStoredPrompts(typeKey: string): ExploreTypePrompt {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    const stored = data[typeKey];
    if (stored?.part1 || stored?.part2 || stored?.part3 || stored?.part4) return stored;
  } catch { /* ignore */ }
  return DEFAULT_PROMPTS[typeKey] || { part1: "", part2: "", part3: "", part4: "" };
}

/** Defaults for new EAD Explore projects. Login Info (part1) is always empty — set per template. */
export function getJobTypePromptParts(typeKey: string): {
  aiPromptPart1: string;
  aiPromptPart2: string;
  aiPromptPart3: string;
  aiPromptPart4: string;
} {
  const p = getStoredPrompts(typeKey);
  if (isPfmExploreType(typeKey)) {
    return {
      aiPromptPart1: "",
      aiPromptPart2: p.part2 || "",
      aiPromptPart3: "",
      aiPromptPart4: "",
    };
  }
  return {
    aiPromptPart1: p.part1 || "",
    aiPromptPart2: p.part2 || "",
    aiPromptPart3: p.part3 || "",
    aiPromptPart4: p.part4 || "",
  };
}

function saveStoredPrompts(typeKey: string, prompts: ExploreTypePrompt) {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    data[typeKey] = prompts;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch { /* ignore */ }
}

@customElement("ead-explore-types-view")
export class EadExploreTypesView extends LitElement {
  static styles = css`
    :host { display: block; height: 100%; min-height: 0; }
    .container { display: flex; flex-direction: column; height: 100%; }
    .header { padding: 20px 28px 16px; border-bottom: 1px solid var(--border, #2e2f33); }
    .header h2 { margin: 0; font-size: 18px; font-weight: 600; }
    .header p { margin: 8px 0 0; font-size: 12px; color: var(--muted, #8b8d93); }
    .tabs { display: flex; gap: 4px; padding: 12px 28px 0; border-bottom: 1px solid var(--border, #2e2f33); overflow-x: auto; }
    .tab { padding: 8px 18px; font-size: 13px; font-weight: 500; border-radius: 6px 6px 0 0; border: none; cursor: pointer; background: transparent; color: var(--muted, #8b8d93); }
    .tab.active { background: var(--bg-surface-3, rgba(255,255,255,0.08)); color: var(--fg, #e4e5e7); border-bottom: 2px solid var(--accent, #6ea8fe); }
    .content {
      flex: 1;
      min-height: 0;
      padding: 24px 28px;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .content.editing { padding-bottom: 20px; }
    .toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      flex-shrink: 0;
    }
    .toolbar h3 { margin: 0; font-size: 15px; }
    .btn { padding: 6px 14px; border-radius: 6px; border: 1px solid var(--border, #2e2f33); background: transparent; color: var(--fg, #e4e5e7); font-size: 12px; cursor: pointer; }
    .btn:hover { background: rgba(255,255,255,0.05); }
    .btn-primary { background: var(--accent, #6ea8fe); color: #fff; border-color: var(--accent, #6ea8fe); }
    .editor-wrap {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      gap: 16px;
      overflow: hidden;
    }
    .prompt-preview {
      flex: 1;
      min-height: 0;
      overflow: auto;
      background: rgba(30,31,34,0.5);
      border: 1px solid rgba(255,255,255,0.08);
      border-radius: 8px;
      padding: 20px;
      font-size: 14px;
      line-height: 1.7;
    }
    .phase-editor {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
    .phase-editor label {
      display: block;
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 8px;
      flex-shrink: 0;
    }
    .phase-editor textarea {
      flex: 1;
      min-height: 0;
      width: 100%;
      box-sizing: border-box;
      resize: none;
      font-family: 'SF Mono', Monaco, monospace;
      font-size: 13px;
      line-height: 1.6;
      padding: 12px;
      border-radius: 8px;
      border: 1px solid var(--border, #2e2f33);
      background: var(--bg-surface, #222326);
      color: var(--fg, #e4e5e7);
    }
  `;

  @state() private _activeTab = "pfm-full";
  @state() private _editing = false;
  @state() private _draft: ExploreTypePrompt = {};

  private _previewMarkdown(prompts: ExploreTypePrompt, isPfm: boolean) {
    if (isPfm) {
      return prompts.part2?.trim() ? `## Explore\n\n${prompts.part2.trim()}` : "No Explore prompt defined. Click Edit to add one.";
    }
    const parts = PART_LABELS_SIMPLE;
    const sections = parts
      .map((p, i) => {
        const body = [prompts.part1, prompts.part2][i]?.trim();
        return body ? `## ${p.title}\n\n${body}` : "";
      })
      .filter(Boolean);
    return sections.join("\n\n---\n\n") || "No prompt defined. Click Edit to add one.";
  }

  render() {
    const prompts = this._editing ? (this._draft || getStoredPrompts(this._activeTab)) : getStoredPrompts(this._activeTab);
    const isPfm = isPfmExploreType(this._activeTab);
    const parts = isPfm ? PART_LABELS_PFM : PART_LABELS_SIMPLE;
    const activeType = EXPLORE_TYPES.find((t) => t.key === this._activeTab);

    return html`
      <div class="container">
        <div class="header">
          <h2>Define Explore Job Types</h2>
          <p>Explore-type defaults only. Login credentials are configured on each project template.</p>
        </div>
        <div class="tabs">
          ${EXPLORE_TYPES.map((t) => html`
            <button class="tab ${t.key === this._activeTab ? "active" : ""}" @click=${() => { this._activeTab = t.key; this._editing = false; }}>${t.label}</button>
          `)}
        </div>
        <div class="content ${this._editing ? "editing" : ""}">
          <div class="toolbar">
            <h3>${activeType?.label} — ${activeType?.desc}</h3>
            ${this._editing
              ? html`
                <div style="display:flex;gap:8px;">
                  <button class="btn btn-primary" @click=${() => { saveStoredPrompts(this._activeTab, this._draft); this._editing = false; }}>Save</button>
                  <button class="btn" @click=${() => { this._editing = false; }}>Cancel</button>
                </div>`
              : html`<button class="btn" @click=${() => { this._draft = getStoredPrompts(this._activeTab); this._editing = true; }}>Edit</button>`
            }
          </div>
          ${this._editing
            ? html`<div class="editor-wrap">${parts.map((p) => html`
              <div class="phase-editor">
                <label>${p.title}</label>
                <textarea
                  placeholder=${p.placeholder}
                  .value=${(this._draft as Record<string, string>)?.[p.key] || ""}
                  @input=${(e: Event) => {
                    const d = { ...this._draft };
                    d[p.key as keyof ExploreTypePrompt] = (e.target as HTMLTextAreaElement).value;
                    this._draft = d;
                  }}
                ></textarea>
              </div>`)}
            </div>`
            : html`<div class="prompt-preview">${unsafeHTML(toSanitizedMarkdownHtml(this._previewMarkdown(prompts, isPfm)))}</div>`
          }
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-explore-types-view": EadExploreTypesView;
  }
}
